import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getInvoice, isMedicineBill } from "../../core/api/invoiceService";
import { getPatient } from "../../core/api/patientService";
import { getDoctor } from "../../core/api/doctorService";
import { getTeeth } from "../../core/api/toothService";
import { getShades } from "../../core/api/shadeService";
import { CLINIC_INFO } from "../../core/constants/clinicInfo";
import { formatAge } from "../../core/utils/calculateAge";

const fmt = (v) => Number(v || 0).toLocaleString("en-IN");
const formatDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
};

const InvoicePrint = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [patient, setPatient] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [teethById, setTeethById] = useState(new Map());
  const [shadesById, setShadesById] = useState(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getInvoice(id)
      .then(async (res) => {
        if (cancelled) return;
        const inv = res.data;
        setInvoice(inv);
        const patientId = inv.patientID ?? inv.patientId;
        const doctorId = inv.doctorID ?? inv.doctorId;
        const [patientRes, doctorRes, teethRes, shadesRes] = await Promise.all([
          patientId ? getPatient(patientId).catch(() => null) : Promise.resolve(null),
          doctorId ? getDoctor(doctorId).catch(() => null) : Promise.resolve(null),
          getTeeth().catch(() => null),
          getShades().catch(() => null),
        ]);
        if (cancelled) return;
        if (patientRes) setPatient(patientRes.data);
        if (doctorRes) setDoctor(doctorRes.data);
        if (teethRes) setTeethById(new Map(teethRes.data.map((t) => [t.id, t.toothName])));
        if (shadesRes) setShadesById(new Map(shadesRes.data.map((s) => [s.id, s.shadeName])));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  if (!invoice) return <div className="page-wrapper"><div className="content"><p>Invoice not found.</p></div></div>;

  // A procedure line can be charged against several teeth — render them comma-joined,
  // falling back to the API's current single toothID.
  const toothLabel = (l) => {
    const ids = Array.isArray(l.toothIDs ?? l.toothIds)
      ? (l.toothIDs ?? l.toothIds)
      : [l.toothID ?? l.toothId].filter(Boolean);
    const names = ids.map((t) => teethById.get(t)).filter(Boolean);
    return names.length ? names.join(", ") : "—";
  };

  // Medicine bills have product lines only, so the tooth/shade columns are dropped.
  const medicine = isMedicineBill(invoice);

  const rows = [
    ...(invoice.procedureLines || []).map((l) => ({
      name: l.procedureName || l.name || "Procedure",
      tooth: toothLabel(l),
      shade: shadesById.get(l.shadeID ?? l.shadeId) || "—",
      qty: l.qty ?? l.quantity ?? 1,
      rate: l.rate,
      amount: l.amount,
    })),
    ...(invoice.productLines || []).map((l) => ({
      name: l.itemName || l.productName || l.name || "Product",
      tooth: "—",
      shade: "—",
      qty: l.qty ?? l.quantity ?? 1,
      rate: l.rate,
      amount: l.amount,
    })),
  ];

  return (
    <div className="page-wrapper invoice-print-page">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .header, #sidebar, .page-header, .no-print { display: none !important; }
          .page-wrapper { margin: 0 !important; padding: 0 !important; }
          .invoice-print-area { box-shadow: none !important; min-height: 100vh; }
          body { background: #fff !important; }
        }
        .invoice-print-area { max-width: 820px; margin: 0 auto; background: #fff; padding: 40px; font-family: Arial, sans-serif; color: #000; display: flex; flex-direction: column; }
        .invoice-print-area table { width: 100%; border-collapse: collapse; }
        .invoice-print-area th, .invoice-print-area td { border: 1px solid #000; padding: 8px 10px; }
        .invoice-print-area .patient-box { border: 1px solid #000; padding: 12px 16px; margin-bottom: 30px; }
        .invoice-print-area .patient-box .row { display: flex; justify-content: space-between; margin-bottom: 8px; }
        .invoice-print-area .patient-box .row:last-child { margin-bottom: 0; }
        .invoice-print-area .summary-table { width: 260px; margin-left: auto; margin-top: -1px; }
        .invoice-print-area .summary-table td:first-child { font-weight: 600; }
        .invoice-print-area .summary-table td:last-child { text-align: right; }
        .invoice-print-area .summary-table .grand-total td { font-weight: bold; font-size: 1.05em; }
        .invoice-print-area .signature { text-align: right; margin-top: 40px; font-weight: 600; }
        .invoice-print-area .print-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; }
        .invoice-print-area .print-logo { max-height: 70px; }
        .invoice-print-area .doctor-block { text-align: right; }
        .invoice-print-area .doctor-block .doctor-entry { margin-bottom: 8px; }
        .invoice-print-area .doctor-block .doctor-entry:last-child { margin-bottom: 0; }
        .invoice-print-area .doctor-block .doctor-name { font-weight: bold; font-size: 1.02em; }
        .invoice-print-area .doctor-block .doctor-qual { font-size: 0.82em; line-height: 1.35; color: #222; }
        .invoice-print-area .print-rule { border: none; border-top: 2px solid #000; margin: 0 0 24px; }
        .invoice-print-area .payments-mini-table { width: 360px; margin-top: 20px; }
        .invoice-print-area .payments-mini-table th, .invoice-print-area .payments-mini-table td { padding: 6px 8px; font-size: 0.9em; }
        .invoice-print-area .payments-title { font-weight: 600; margin: 20px 0 6px; }
        .invoice-print-area .payment-summary-row { display: flex; justify-content: space-between; align-items: flex-start; margin-top: 20px; gap: 20px; }
        .invoice-print-area .print-footer { margin-top: auto; padding-top: 40px; font-size: 0.82em; color: #333; }
        .invoice-print-area .print-footer-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
        .invoice-print-area .print-footer-dots span { display: inline-block; width: 6px; height: 6px; border-radius: 50%; margin-right: 4px; }
        .invoice-print-area .print-footer-strapline { font-style: italic; color: #555; }
        .invoice-print-area .print-footer-rule { border: none; border-top: 1px solid #999; margin: 0 0 8px; }
        .invoice-print-area .print-footer-bottom { display: flex; justify-content: space-between; align-items: flex-end; }
        .invoice-print-area .print-footer-bottom .print-footer-right { text-align: right; }
        .invoice-print-area .print-footer .clinic-name-footer { font-weight: bold; font-size: 1.05em; color: #000; margin-bottom: 2px; }
      `}</style>

      <div className="content no-print d-flex justify-content-end gap-2 mb-3">
        <button
          className="btn btn-cancel"
          onClick={() => {
            // Print is always opened via window.open(..., "_blank") from the invoice
            // list/form, so this tab has no in-app history to go back to — close the
            // tab if it was opened that way, otherwise fall back to the invoice list.
            if (window.opener) window.close();
            else navigate(medicine ? "/medicine-bills" : "/invoices");
          }}
        >
          Back
        </button>
        <button className="btn btn-submit" onClick={() => window.print()}>Print</button>
      </div>

      <div className="invoice-print-area">
        <div className="print-header">
          <img src={CLINIC_INFO.logo} alt={CLINIC_INFO.clinicName} className="print-logo" />
          <div className="doctor-block">
            {CLINIC_INFO.doctors.map((doc, idx) => (
              <div className="doctor-entry" key={idx}>
                <div className="doctor-name">{doc.name}</div>
                {doc.qualifications.map((q, qIdx) => (
                  <div className="doctor-qual" key={qIdx}>{q}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <hr className="print-rule" />

        {medicine && (
          <h5 style={{ textAlign: "center", fontWeight: "bold", margin: "0 0 16px" }}>
            MEDICINE BILL{invoice.invoiceNumber ? ` — ${invoice.invoiceNumber}` : ""}
          </h5>
        )}

        <div className="patient-box">
          <div className="row">
            <span>PATIENT NAME : {patient?.patientName || invoice.patientName || "—"}</span>
            <span>AGE:{formatAge(patient?.dob, patient?.age) ?? "—"}/{patient?.gender || "—"}</span>
          </div>
          <div className="row">
            <span>REGISTER NUMBER:{patient?.opNumber || "—"}</span>
            <span>DATE:{formatDate(invoice.invoiceDate)}</span>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style={{ width: 60 }}>SL.NO</th>
              <th>{medicine ? "MEDICINE" : "TREATMENT DONE"}</th>
              {!medicine && <th style={{ width: 90 }}>TOOTH</th>}
              {!medicine && <th style={{ width: 90 }}>SHADE</th>}
              <th style={{ width: 100 }}>QUANTITY</th>
              <th style={{ width: 100 }}>RATE</th>
              <th style={{ width: 120 }}>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={medicine ? 5 : 7} style={{ textAlign: "center" }}>No line items</td></tr>
            ) : rows.map((r, idx) => (
              <tr key={idx}>
                <td style={{ textAlign: "center" }}>{idx + 1}</td>
                <td>{r.name}</td>
                {!medicine && <td style={{ textAlign: "center" }}>{r.tooth}</td>}
                {!medicine && <td style={{ textAlign: "center" }}>{r.shade}</td>}
                <td style={{ textAlign: "center" }}>{String(r.qty).padStart(2, "0")}</td>
                <td style={{ textAlign: "right" }}>{fmt(r.rate)}</td>
                <td style={{ textAlign: "right" }}>{fmt(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="payment-summary-row">
          {invoice.payments?.length > 0 && (
            <div>
              <div className="payments-title">Payments Collected</div>
              <table className="payments-mini-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Mode</th>
                    <th>Reference #</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.payments.map((p, idx) => (
                    <tr key={idx}>
                      <td>{formatDate(p.paymentDate)}</td>
                      <td>{p.paymentMode || "Cash"}</td>
                      <td>{p.transactionRef || "—"}</td>
                      <td style={{ textAlign: "right" }}>{fmt(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <table className="summary-table">
            <tbody>
              <tr className="grand-total">
                <td>Total Amount</td>
                <td>{fmt(invoice.netTotal)}</td>
              </tr>
              <tr>
                <td>Total Paid</td>
                <td>{fmt(invoice.paidAmount)}</td>
              </tr>
              <tr>
                <td>Balance</td>
                <td>{fmt(invoice.balanceAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {doctor?.doctorName && <div className="signature"> {doctor.doctorName}</div>}

        <div className="print-footer">
          <div className="print-footer-top">
            <div className="print-footer-dots">
              <span style={{ background: "#f2c94c" }} />
              <span style={{ background: "#e29b9b" }} />
              <span style={{ background: "#8fbf8f" }} />
              <span style={{ background: "#8fb8d9" }} />
            </div>
            <div className="print-footer-strapline">{CLINIC_INFO.strapline}</div>
          </div>
          <hr className="print-footer-rule" />
          <div className="print-footer-bottom">
            <div className="print-footer-left">
              <div className="clinic-name-footer">{CLINIC_INFO.clinicName}</div>
              <div>{CLINIC_INFO.tagline}</div>
              <div>{CLINIC_INFO.addressLines.join(", ")}</div>
            </div>
            <div className="print-footer-right">
              <div>{CLINIC_INFO.website}</div>
              <div>{CLINIC_INFO.phones.join(" | ")}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePrint;
